import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Menu, Zap, LogOut, LayoutDashboard, ShieldCheck, Ticket, Crown } from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/katalog", label: "Katalog" },
  { to: "/hitung", label: "Hitung Hemat" },
  { to: "/tanjakan", label: "Cek Tanjakan" },
  { to: "/bandingkan", label: "Bandingkan" },
];

export function Header() {
  const { user, logout, isPremium } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const loggedIn = user && user !== null && user !== false;

  const roleLabel = { super_admin: "Super Admin", admin: "Admin", editor: "Editor" };
  const CreditPill = () => {
    if (!loggedIn) return null;
    if (["super_admin", "admin", "editor"].includes(user.role)) {
      return (
        <span data-testid="staff-pill" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-full">
          <ShieldCheck className="w-4 h-4" /> {roleLabel[user.role]}
        </span>
      );
    }
    if (isPremium()) {
      return (
        <span data-testid="premium-pill" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 bg-amber-50 px-3 py-1.5 rounded-full">
          <Crown className="w-4 h-4" /> Premium
        </span>
      );
    }
    return (
      <span data-testid="credit-pill" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-teal bg-accent px-3 py-1.5 rounded-full">
        <Ticket className="w-4 h-4" /> {user.trial_credits ?? 0} analisis gratis
      </span>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 font-heading font-extrabold text-navy text-lg">
          <span className="w-9 h-9 rounded-xl bg-teal flex items-center justify-center text-white"><Zap className="w-5 h-5" strokeWidth={2.5} /></span>
          Beli<span className="text-teal">Motor Listrik</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${location.pathname === n.to ? "text-teal bg-accent" : "text-navy hover:bg-secondary"}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <CreditPill />
          {loggedIn ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="outline" className="rounded-full h-10 w-10 p-0 font-heading font-bold">
                  {(user.name || "U").charAt(0).toUpperCase()}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <div className="px-3 py-2 text-sm">
                  <p className="font-semibold text-navy truncate">{user.name}</p>
                  <p className="text-muted-foreground text-xs truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}><LayoutDashboard className="w-4 h-4 mr-2" />Dashboard</DropdownMenuItem>
                {["super_admin", "admin", "editor"].includes(user.role) && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}><ShieldCheck className="w-4 h-4 mr-2" />Panel Admin</DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-upgrade" onClick={() => navigate("/premium")}><Crown className="w-4 h-4 mr-2" />Upgrade Premium</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}><LogOut className="w-4 h-4 mr-2" />Keluar</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button data-testid="login-btn" variant="ghost" onClick={() => navigate("/login")} className="rounded-full">Masuk</Button>
              <Button data-testid="register-btn" onClick={() => navigate("/register")} className="rounded-full bg-teal hover:bg-teal-hover">Coba Gratis</Button>
            </div>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="mobile-menu-btn" variant="ghost" size="icon" className="md:hidden rounded-full"><Menu className="w-5 h-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white w-72">
              <div className="flex flex-col gap-1 mt-8">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.to.slice(1)}`}
                    className="px-4 py-3 rounded-xl font-medium text-navy hover:bg-secondary">{n.label}</Link>
                ))}
                <div className="h-px bg-border my-3" />
                {!loggedIn && (
                  <>
                    <Button data-testid="mobile-login-btn" variant="outline" className="rounded-full" onClick={() => { setOpen(false); navigate("/login"); }}>Masuk</Button>
                    <Button data-testid="mobile-register-btn" className="rounded-full bg-teal hover:bg-teal-hover mt-2" onClick={() => { setOpen(false); navigate("/register"); }}>Coba Gratis</Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
